import React from "react";
import PageHeader from "../components/PageHeader";
import { useReveal } from "../hooks/useReveal";
import { FileText, CheckCircle, Info, Presentation, Award, Send } from "lucide-react";

const Abstract = () => {
  useReveal();
  return (
    <div>
      <PageHeader title="Abstract Submission" crumb="Abstract" subtitle="We welcome you to submit your abstracts for the upcoming DRS 2026 conference." />

      <section className="section-pad bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          
          <div className="bg-slate-50 border border-slate-100 rounded-3xl p-8 md:p-12 shadow-sm mb-12 reveal-up">
            <div className="flex flex-col md:flex-row items-start gap-6">
              <div className="w-14 h-14 shrink-0 bg-brand/10 text-brand rounded-2xl flex items-center justify-center">
                <Info size={28} />
              </div>
              <div>
                <h3 className="font-display font-bold text-navy-900 text-2xl md:text-3xl mb-4">Guidelines & Information</h3>
                <p className="text-slate-600 text-lg leading-relaxed mb-6">
                  We are pleased to invite all interested individuals to submit an abstract for DRS 2026. For presenting authors of accepted abstracts, registration and on-site participation is mandatory.
                </p>
                {/* 
                <div className="bg-brand/5 border border-brand/10 rounded-xl p-5 text-brand-700 font-medium flex items-start gap-3">
                  <Award size={24} className="shrink-0 mt-0.5" />
                  <p className="text-base">The three best poster authors and three best oral presenters will receive free registration for the next year's conference.</p>
                </div>
                */}
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-white border border-slate-100 rounded-3xl p-8 md:p-10 shadow-sm hover:shadow-md transition-shadow reveal-up" style={{ transitionDelay: "100ms" }}>
              <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mb-6">
                <FileText size={24} />
              </div>
              <h3 className="font-display font-bold text-navy-900 text-2xl mb-4">Abstract Structure</h3>
              <p className="text-slate-600 mb-6 leading-relaxed text-lg">
                Submissions can be made in one of the following three categories: oral presentation, scientific poster, or educational poster.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-slate-700 text-lg">
                  <CheckCircle size={20} className="text-green-500" />
                  <span><strong>Limit:</strong> Max 300 words</span>
                </li>
              </ul>
            </div>

            <div className="bg-white border border-slate-100 rounded-3xl p-8 md:p-10 shadow-sm hover:shadow-md transition-shadow reveal-up" style={{ transitionDelay: "200ms" }}>
              <div className="w-12 h-12 bg-purple-50 text-purple-600 rounded-2xl flex items-center justify-center mb-6">
                <Presentation size={24} />
              </div>
              <h3 className="font-display font-bold text-navy-900 text-2xl mb-6">Types of Abstracts</h3>
              <ul className="space-y-6 text-slate-600">
                <li className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-blue-50 text-blue-500 flex items-center justify-center shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 2v7.31"/><path d="M14 9.3V1.99"/><path d="M8.5 2h7"/><path d="M14 9.3a6.5 6.5 0 1 1-4 0"/><path d="M5.52 16h12.96"/></svg>
                  </div>
                  <div>
                    <h4 className="font-bold text-navy-900 text-lg mb-1">Science</h4>
                    <p className="text-slate-600 leading-relaxed">Hypothesis-driven research. Can be oral or digital presentation.</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-amber-50 text-amber-500 flex items-center justify-center shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
                  </div>
                  <div>
                    <h4 className="font-bold text-navy-900 text-lg mb-1">Educational</h4>
                    <p className="text-slate-600 leading-relaxed">Teach or review topics. Can be digital or standalone exhibits. Must have Learning Objectives, Content Organization, and Take-home Messages.</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  </div>
                  <div>
                    <h4 className="font-bold text-navy-900 text-lg mb-1">Please note</h4>
                    <p className="text-slate-600 leading-relaxed">Quality Improvement Reports should be submitted under the Science category.</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-slate-50 border border-slate-100 rounded-3xl p-8 md:p-12 shadow-sm mb-12 reveal-up" style={{ transitionDelay: "300ms" }}>
            <h3 className="font-display font-bold text-navy-900 text-2xl md:text-3xl mb-8">Submission & Review Process</h3>
            <div className="space-y-8">
              
              <div className="flex items-start gap-5">
                <div className="w-10 h-10 rounded-full bg-white border border-slate-200 shadow-sm flex items-center justify-center text-slate-600 shrink-0 font-bold text-lg mt-0.5">1</div>
                <div>
                  <h4 className="font-bold text-navy-900 text-xl mb-3">Abstract Submission Overview</h4>
                  <p className="text-slate-600 text-lg leading-relaxed">Submit abstracts for consideration using an online submission program, which allows you to save and edit your work during the submission period. All abstracts must be submitted online by the posted deadlines in order to be considered for acceptance.</p>
                </div>
              </div>

              <div className="flex items-start gap-5">
                <div className="w-10 h-10 rounded-full bg-white border border-slate-200 shadow-sm flex items-center justify-center text-slate-600 shrink-0 font-bold text-lg mt-0.5">2</div>
                <div>
                  <h4 className="font-bold text-navy-900 text-xl mb-3">Abstract Review Process</h4>
                  <p className="text-slate-600 text-lg leading-relaxed">All abstracts are evaluated through a rigorous, double-blind peer-review process to ensure the highest academic and scientific quality.</p>
                </div>
              </div>

              <div className="flex items-start gap-5">
                <div className="w-10 h-10 rounded-full bg-white border border-slate-200 shadow-sm flex items-center justify-center text-slate-600 shrink-0 font-bold text-lg mt-0.5">3</div>
                <div>
                  <h4 className="font-bold text-navy-900 text-xl mb-3">Accepted Oral Presentations</h4>
                  <p className="text-slate-600 text-lg leading-relaxed">Authors of the highest-rated oral abstracts will be invited to present their work. Presentations should be prepared in PowerPoint format with maximum of 15 slides. Must adhere to the Structured format: Background/Purpose, Methods, Results, Conclusion. Please adhere to the allotted speaking time. Upload your presentation on-site at least two hours prior to your session.</p>
                </div>
              </div>

              <div className="flex items-start gap-5">
                <div className="w-10 h-10 rounded-full bg-white border border-slate-200 shadow-sm flex items-center justify-center text-slate-600 shrink-0 font-bold text-lg mt-0.5">4</div>
                <div>
                  <h4 className="font-bold text-navy-900 text-xl mb-3">Accepted Poster Presentations</h4>
                  <p className="text-slate-600 text-lg leading-relaxed">Authors of top-scoring poster abstracts will be invited to display their work. Accepted posters must be prepared in A1 portrait format. Presenters are required to bring printed posters to the venue and mount them on designated poster walls.</p>
                </div>
              </div>

            </div>
          </div>

          <div className="bg-white border border-brand/10 rounded-3xl p-8 md:p-12 shadow-lg shadow-brand/5 text-center reveal-up" style={{ transitionDelay: "400ms" }}>
            <div className="w-20 h-20 bg-brand/10 text-brand rounded-full flex items-center justify-center mx-auto mb-6">
              <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/><path d="M16 13H8"/><path d="M16 17H8"/><path d="M10 9H8"/></svg>
            </div>
            <h2 className="font-display font-bold text-navy-900 text-2xl md:text-3xl mb-4">Submit Your Abstract</h2>
            <p className="text-slate-600 text-lg mb-8 max-w-2xl mx-auto">
              Ready to submit your abstract? Click the button below to proceed to the official abstract submission portal.
            </p>
            <a
              href="https://rxregistrations.com/drs2026/abstract/"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-shine inline-flex items-center gap-3 bg-brand hover:bg-brand-600 text-white font-semibold rounded-full px-10 py-4 text-lg shadow-xl shadow-brand/25 transition-all hover:-translate-y-1"
            >
              Submit Abstract Here
              <Send size={20} />
            </a>
          </div>

        </div>
      </section>
    </div>
  );
};

export default Abstract;
